import { useState, useEffect } from "react"
import { PROJECTS_PER_PAGE } from "config"
import { OrderDirection, Project } from "utils/types"
import Loading from "components/Loading"
import OwnedTokens from "components/OwnedTokens"
import useOwnedProjects from "hooks/useOwnedProjects"
import useCountOwnedProjects from "hooks/useCountOwnedProjects"
import { parseAspectRatio } from "utils/scriptJSON"

interface Props {
  walletAddress: string
}

const OwnedProjects = ({ walletAddress }: Props) => {
  const [countOwnedProjects, setCountOwnedProjects] = useState(0)
  const [currentPage, setCurrentPage] = useState(0)
  const [orderDirection, setOrderDirection] = useState<OrderDirection>(OrderDirection.DESC)
  const skip = currentPage * PROJECTS_PER_PAGE
  const { loading, error, data } = useOwnedProjects(walletAddress, { skip, first: PROJECTS_PER_PAGE, orderDirection })
  const [filteredProjects, setFilteredProjects] = useState<any[]>([])
  const countResponse = useCountOwnedProjects(walletAddress)

  useEffect(() => {
    setFilteredProjects(data?.projects?.filter((p: { tokens: any[] }) => p.tokens.length > 0) ?? [])
    setCountOwnedProjects(
      countResponse.data?.projects?.filter((p: { tokens: any[] }) => p.tokens.length > 0).length ?? 0
    )
  }, [data, countResponse])

  const totalPages = Math.ceil(countOwnedProjects / PROJECTS_PER_PAGE)

  return (
    <div>
      <div className="flex items-center justify-between mb-10">
        <h1 className="gallery-heading">Collection</h1>
        {!loading && !error && filteredProjects?.length > 0 && (
          <select
            value={orderDirection}
            onChange={(e) => {
              setCurrentPage(0)
              setOrderDirection(e.target.value as OrderDirection)
            }}
            className="gallery-label bg-transparent border-b border-gallery-border pb-0.5 focus:outline-none cursor-pointer"
          >
            <option value={OrderDirection.DESC}>Newest</option>
            <option value={OrderDirection.ASC}>Oldest</option>
          </select>
        )}
      </div>

      {loading ? (
        <Loading />
      ) : error ? (
        <p className="text-sm text-red-500">Error loading collection.</p>
      ) : filteredProjects?.length > 0 ? (
        <>
          <div className="space-y-16">
            {filteredProjects.map((project: Project) => (
              <div key={project.id}>
                <a href={`/project/${project.contract.id}/${project.projectId}`} className="block mb-6">
                  <h2 className="font-serif text-2xl text-gallery-text hover:text-gallery-muted transition-colors">
                    {project.name}
                    {project.artistName !== "r4v3n" && (
                      <span className="artist-name text-gallery-muted"> — {project.artistName}</span>
                    )}
                  </h2>
                </a>
                <OwnedTokens
                  contractAddress={project.contract.id}
                  projectId={project.id}
                  walletAddress={walletAddress}
                  aspectRatio={project.aspectRatio || parseAspectRatio(project.scriptJSON)}
                />
              </div>
            ))}
          </div>

          {totalPages > 1 && (
            <div className="flex justify-center items-center gap-4 mt-16 mb-8">
              <button
                disabled={currentPage === 0}
                onClick={() => { window.scrollTo(0, 0); setCurrentPage(currentPage - 1) }}
                className="gallery-label disabled:opacity-30 hover:text-gallery-text transition-colors"
              >
                ← Prev
              </button>
              <span className="gallery-label text-gallery-muted">{currentPage + 1} / {totalPages}</span>
              <button
                disabled={currentPage === totalPages - 1}
                onClick={() => { window.scrollTo(0, 0); setCurrentPage(currentPage + 1) }}
                className="gallery-label disabled:opacity-30 hover:text-gallery-text transition-colors"
              >
                Next →
              </button>
            </div>
          )}
        </>
      ) : (
        <p className="text-sm text-gallery-muted">No works collected yet.</p>
      )}
    </div>
  )
}

export default OwnedProjects
